import React, { useEffect, useState, forwardRef } from "react"; // Added React import
import {
    Container,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useTheme,
    Box,

} from "@mui/material";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from "recharts";
import Ordersdetails from '../comp/OrdersDetails.jsx'


// Wrap component in forwardRef to accept the ref
export const OrderReportPage = forwardRef(({ reportRange, rawData }, ref) => {
    const theme = useTheme();

    const [chartData, setChartData] = useState([]);
    const [topItems, setTopItems] = useState([]);
    const [orderSummary, setOrderSummary] = useState({
        total: 0,       // This might need recalculation depending on source
        dineIn: 0,
        takeOut: 0,
        kiosk: 0,       // Added Kiosk type based on SalesReport logic
        online: 0,      // Renamed for consistency
        other: 0,       // Added Other type
        voided: 0,
        completed: 0,
    });
    // State to hold all completed orders for Ordersdetails
    const [allCompletedOrders, setAllCompletedOrders] = useState([]);

    useEffect(() => {
        // --- MODIFIED LOGIC TO HANDLE ARRAY ---
        console.log("OrderReportPage received rawData:", rawData);

        // Check if rawData is a valid array
        if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
            console.log("OrderReportPage: Did not find valid array data to process.");
            setChartData([]);
            setTopItems([]);
            setOrderSummary({ total: 0, dineIn: 0, takeOut: 0, kiosk: 0, online: 0, other: 0, voided: 0, completed: 0 });
            setAllCompletedOrders([]);
            return;
        }

        const hourlyMap = {};
        const itemMap = {};
        let summary = { total: 0, dineIn: 0, takeOut: 0, kiosk: 0, online: 0, other: 0, voided: 0, completed: 0 };
        let combinedCompletedOrders = [];

        // Loop through each DAY in the rawData array
        rawData.forEach(dayData => {
            summary.voided += dayData.cancelOrders?.length ?? 0;
            summary.total += (dayData.completedOrders?.length ?? 0) + (dayData.cancelOrders?.length ?? 0); // Recalculate total orders

            // Process completed orders for the day
            (dayData.completedOrders || []).forEach(order => {
                summary.completed++;
                combinedCompletedOrders.push(order); // Add to the list for Ordersdetails

                // 1. Chart by hour
                 if (order.startTime) {
                    try {
                        const date = new Date(order.startTime);
                        if (!isNaN(date.getTime())) {
                            const hour = date.getHours(); // 0-23
                             hourlyMap[hour] = (hourlyMap[hour] || 0) + 1;
                        }
                    } catch(e) { console.error("Error parsing order startTime:", order.startTime, e); }
                 }


                // 2. Order summary by type
                switch (order.type) {
                    case 0: summary.dineIn++; break;
                    case 1: summary.takeOut++; break;
                    case 2: summary.kiosk++; break; // Assuming type 2 is Kiosk
                    case 3: summary.online++; break; // Assuming type 3 is Online
                    default: summary.other++; break;
                }

                // 3. Top Items
                // Ensure order.orders exists and is an array
                if (Array.isArray(order.orders)) {
                    order.orders.forEach(o => {
                         // Ensure o.order exists and is an array
                        if (Array.isArray(o.order)) {
                            o.order.forEach(item => {
                                if (item.item && typeof item.item === 'string') { // Check item name validity
                                    itemMap[item.item] = (itemMap[item.item] || 0) + (item.quantity ?? 0);
                                }
                            });
                        }
                    });
                 }
            });
        });
        // --- END OF LOOP ---

        // Finalize chart data (sort by hour)
        const chart = Object.entries(hourlyMap)
            .map(([hour, count]) => ({ hour: parseInt(hour), orders: count })) // Keep hour as number for sorting
            .sort((a, b) => a.hour - b.hour) // Sort numerically
            .map(item => ({ hour: `${item.hour}:00`, orders: item.orders })); // Convert hour to string label


        // Finalize Top Items
        const items = Object.entries(itemMap)
            .map(([item, qty]) => ({ item, qty }))
            .sort((a, b) => b.qty - a.qty)
            .slice(0, 10); // Get top 10

        console.log("OrderReportPage: Processed chart:", chart);
        console.log("OrderReportPage: Processed summary:", summary);
        console.log("OrderReportPage: Processed top items:", items);

        setChartData(chart);
        setOrderSummary(summary);
        setTopItems(items);
        setAllCompletedOrders(combinedCompletedOrders); // Set combined orders for detail view

    }, [rawData, reportRange]); // Keep reportRange if used elsewhere

    return (
        // Attach the ref to the main container
        <Container sx={{ mt: 4, mb: 4 }} ref={ref}>
            {/* 1. Order Timeline */}
            <Paper elevation={3} sx={{ p: 3, mb: 3, backgroundColor: "#f3faff" }}>
                <Typography variant="h6" color="primary" gutterBottom>
                    Orders by Hour
                </Typography>
                 {/* Ensure chartData has data before rendering */}
                {chartData.length > 0 ? (
                    <Box sx={{ height: 300 }}> {/* Use Box with fixed height for ResponsiveContainer */}
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="hour" />
                                <YAxis allowDecimals={false} label={{ value: 'Orders', angle: -90, position: 'insideLeft' }} />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="orders" stroke={theme.palette.primary.main} strokeWidth={2} name="Number of Orders"/>
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>
                ) : (
                    <Typography sx={{ textAlign: 'center', py: 4 }}>No hourly order data available.</Typography>
                )}
            </Paper>

            <Grid container spacing={3}>
                {/* 2. Order Summary */}
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, backgroundColor: "#fff3e0", height: '100%' }}>
                        <Typography variant="h6" color="warning.main" gutterBottom>
                            Order Summary
                        </Typography>
                        <Table size="small">
                            <TableBody>
                                <TableRow><TableCell>Total Orders (Completed + Void)</TableCell><TableCell align="right">{orderSummary.total}</TableCell></TableRow>
                                <TableRow><TableCell>Completed Orders</TableCell><TableCell align="right">{orderSummary.completed}</TableCell></TableRow>
                                <TableRow><TableCell>Voided Orders</TableCell><TableCell align="right">{orderSummary.voided}</TableCell></TableRow>
                                <TableRow><TableCell sx={{ pl: 3 }}>- Dine In</TableCell><TableCell align="right">{orderSummary.dineIn}</TableCell></TableRow>
                                <TableRow><TableCell sx={{ pl: 3 }}>- Take Out</TableCell><TableCell align="right">{orderSummary.takeOut}</TableCell></TableRow>
                                <TableRow><TableCell sx={{ pl: 3 }}>- Kiosk</TableCell><TableCell align="right">{orderSummary.kiosk}</TableCell></TableRow>
                                <TableRow><TableCell sx={{ pl: 3 }}>- Online</TableCell><TableCell align="right">{orderSummary.online}</TableCell></TableRow>
                                <TableRow><TableCell sx={{ pl: 3 }}>- Other</TableCell><TableCell align="right">{orderSummary.other}</TableCell></TableRow>
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>

                {/* 3. Top Items */}
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, backgroundColor: "#e3f2fd", height: '100%' }}>
                        <Typography variant="h6" color="info.main" gutterBottom>
                            Top Selling Items
                        </Typography>
                        {topItems.length > 0 ? (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Item</TableCell>
                                        <TableCell align="right">Quantity</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {topItems.map(row => (
                                        <TableRow key={row.item}>
                                            <TableCell>{row.item}</TableCell>
                                            <TableCell align="right">{row.qty}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                         ) : (
                             <Typography sx={{ textAlign: 'center', py: 4 }}>No item data available.</Typography>
                         )}
                    </Paper>
                </Grid>
            </Grid>

            {/* 4. Orders Details Section */}
            <Paper elevation={3} sx={{ p: 3, my: 3, backgroundColor: "#f0f4c3" }}> {/* Changed background color */}
                <Typography variant="h6" color="success.dark" gutterBottom> {/* Changed color */}
                    Completed Order Details
                </Typography>
                {/* Pass the combined list of completed orders */}
                <Ordersdetails orders={allCompletedOrders} showPagination={false} />
            </Paper>
        </Container>
    );
});

